import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError, useAuth } from '@halo/web-lib';
import { useDateRange } from '../context/DateRangeContext';
import { startOfMonth, endOfMonth, addMonths, format } from 'date-fns';
import type { DayQuality, DataQualityResponse } from './use-data-quality';

export type { DayQuality };

/**
 * Fetch data quality for the two months visible in the calendar picker.
 *
 * Unlike `useDataQuality` (which tracks the selected range), this follows
 * the calendar viewport so the user sees quality dots while browsing months.
 *
 * Returns:
 * - `qualityMap`: Map<'yyyy-MM-dd', DayQuality> for tooltip lookups
 * - `fullDates`, `partialDates`, `missingDates`: Date arrays for rdp modifiers
 */
export function useCalendarQuality(visibleMonth: Date, options?: { enabled?: boolean }) {
  const { timeZone } = useDateRange();
  const { storeId } = useAuth();
  const enabled = options?.enabled ?? true;

  // Cover both visible months (the calendar shows 2 side by side)
  const from = format(startOfMonth(visibleMonth), 'yyyy-MM-dd');
  const to = format(endOfMonth(addMonths(visibleMonth, 1)), 'yyyy-MM-dd');

  const query = useQuery<DataQualityResponse, ApiError>({
    queryKey: ['data-quality', 'calendar', storeId, from, to, timeZone],
    staleTime: 5 * 60 * 1000,
    enabled,
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ from, to, timezone: timeZone });
      const res = await api.get<DataQualityResponse>(
        `/api/v1/ingestion/data-quality?${qs.toString()}`,
        { signal },
      );

      if (!res.success || res.data === undefined) {
        throw new ApiError(
          res.error ?? 'Failed to fetch calendar quality',
          res.code ?? 'UNKNOWN',
          0,
        );
      }

      return res.data;
    },
  });

  const { qualityMap, fullDates, partialDates, missingDates } = useMemo(() => {
    const map = new Map<string, DayQuality>();
    const full: Date[] = [];
    const partial: Date[] = [];
    const missing: Date[] = [];

    if (!query.data) return { qualityMap: map, fullDates: full, partialDates: partial, missingDates: missing };

    for (const [dateStr, day] of Object.entries(query.data.days)) {
      map.set(dateStr, day);
      const d = new Date(dateStr + 'T00:00:00');

      // Use Shopify status for the dot (primary revenue platform).
      // Fall back to overall status if Shopify isn't connected.
      const shopify = day.platforms['shopify'];
      const primary = shopify ? shopify.status : day.status;

      if (primary === 'full') {
        full.push(d);
      } else if (primary === 'partial') {
        partial.push(d);
      } else if (primary === 'missing') {
        missing.push(d);
      }
    }

    return { qualityMap: map, fullDates: full, partialDates: partial, missingDates: missing };
  }, [query.data]);

  return { qualityMap, fullDates, partialDates, missingDates, isLoading: query.isLoading };
}
